import type { PublicUser } from "@rhema/client";
import { Button, MailIcon, TextField } from "@rhema/ui";
import { type FormEvent, useState } from "react";
import { useApi } from "../session/session";

/**
 * Finds someone by the exact email address they use for Rhema. Servers without phone sign-in use
 * this in place of a phone number. It never lists people by a partial address or a name.
 */
export function FindByEmail({
  label,
  action,
  busyText,
  autoFocus = false,
  onFound,
}: {
  label: string;
  action: string;
  busyText: string;
  autoFocus?: boolean;
  onFound: (person: PublicUser) => void;
}) {
  const api = useApi();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!address.includes("@")) {
      setError("Enter the full email address, like name@example.com.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const [person] = await api.findByEmail(address);
      if (person) onFound(person);
      else setError("No one on Rhema uses that email address. Check it and try again.");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} aria-label="Find someone by email address" className="mt-4 flex flex-col gap-2">
      <TextField
        label={label}
        type="email"
        autoComplete="off"
        inputMode="email"
        autoFocus={autoFocus}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={busy}
      />
      {error && (
        <p role="alert" className="m-0 text-sm text-scarlet">
          {error}
        </p>
      )}
      <Button type="submit" variant="tonal" disabled={busy || !email.trim()} icon={<MailIcon size={17} />}>
        {busy ? busyText : action}
      </Button>
    </form>
  );
}
